import { EditIcon } from 'assets/Icons';
import { EditAddressModal } from 'components/molecules';
import QUERY_KEYS from 'network/config/queryKeys';
import { getAllAddress } from 'network/services/address';
import React from 'react';
import { useQuery } from 'react-query';
import { useRecoilState } from 'recoil';
import { checkoutDetailsAtom } from 'store/atoms/checkoutAtom';
import { contactState } from 'store/atoms/contactAtom';
import { AddressResponse } from 'TYPES/api.body';

const ContactDetails = () => {
  const [addressInfo, setAddressInfo] = useRecoilState(contactState);
  const [checkoutDetails, setCheckoutDetails] = useRecoilState(checkoutDetailsAtom);

  const { data: addressData, isFetchedAfterMount } = useQuery({
    queryFn: getAllAddress,
    queryKey: QUERY_KEYS.CUSTOMER_ADDRESSES
  });

  const addresses = React.useMemo(() => {
    if (!addressData?.data) return [];
    return addressData.data;
  }, [addressData?.data]);

  // Get the default address or first address if no default
  const defaultAddress = React.useMemo(() => {
    const defAddr = addresses.find(item => item.status === true);
    return defAddr || addresses[0];
  }, [addresses]);

  // Set initial address ID when component mounts or addresses change
  React.useEffect(() => {
    if (defaultAddress && !checkoutDetails.addressId) {
      setCheckoutDetails(prev => ({
        ...prev,
        addressId: defaultAddress.id
      }));
    }
  }, [defaultAddress, checkoutDetails.addressId, setCheckoutDetails]);

  const selectedAddress = React.useMemo(() => {
    return addresses.find(addr => addr.id === checkoutDetails.addressId) || defaultAddress;
  }, [addresses, checkoutDetails.addressId, defaultAddress]);

  const handleAddressChange = (addressId: number) => {
    setCheckoutDetails(prev => ({
      ...prev,
      addressId: addressId
    }));
  };

  const showAddressModal = (item: AddressResponse) => {
    setAddressInfo({
      isEditAddressModal: true,
      ...item,
      id: item.id.toString(),
      city: item.city ?? ''
    });
  };

  const addNewAddress = () => {
    setAddressInfo({
      isEditAddressModal: true,
      id: '',
      lastname: '',
      firstname: '',
      street: '',
      city: '',
      state: '',
      country: '',
      phoneNumber: '',
      status: false
    });
  };

  if (!isFetchedAfterMount) {
    return <div>Loading addresses...</div>;
  }

  return (
    <>
      <div className="contact-details-section">
        <div className="flex items-center justify-between mb-4">
          <p className="sub-head">Delivery Address</p>
          <button
            type="button"
            onClick={addNewAddress}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium">
            + Add New Address
          </button>
        </div>

        {addresses.length === 0 ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <p className="text-gray-500 mb-3">No delivery addresses found</p>
            <button
              type="button"
              onClick={addNewAddress}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
              Add Delivery Address
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {addresses.map(item => (
              <div
                key={item.id}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                  checkoutDetails.addressId === item.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handleAddressChange(item.id)}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="mt-1">
                      <input
                        type="radio"
                        name="deliveryAddress"
                        checked={checkoutDetails.addressId === item.id}
                        onChange={() => handleAddressChange(item.id)}
                        className="text-blue-600"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-gray-900">{item.street}</p>
                        {item.status && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Default</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        {item.city && `${item.city}, `}
                        {item.state}, {item.country}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">{item.phoneNumber}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
                    onClick={e => {
                      e.stopPropagation();
                      showAddressModal(item);
                    }}>
                    <EditIcon width={16} />
                    <span>Edit</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Display selected address summary */}
        {selectedAddress && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700">Selected delivery address:</p>
            <p className="text-sm text-gray-600">
              {selectedAddress.street}, {selectedAddress.city && `${selectedAddress.city}, `}
              {selectedAddress.state}, {selectedAddress.country}
            </p>
          </div>
        )}
      </div>

      {addressInfo.isEditAddressModal && <EditAddressModal />}
    </>
  );
};

export default ContactDetails;
